import React, { useState } from "react";
import { Button, ButtonGroup } from "@datapay-by-datacom/design-system";
import { FileDropzone } from "../../../shared/components";
import {
  UploadItem,
  MAX_QUEUE_SIZE,
} from "../../hooks/OrgDocument/useUploadQueue";
import styles from "./OrgDocumentUploadStagingArea.module.css";

type FileUploadResult = {
  total: number;
  accepted: number;
  rejected: number;
  queueLimitReached?: boolean;
  queueLimitExceeded?: boolean;
};

interface OrgDocumentUploadStagingAreaProps {
  queue: UploadItem[];
  isProcessing: boolean;
  processQueue: () => void;
  removeQueueItem: (index: number) => void;
  addFiles: (files: File[]) => FileUploadResult;
}

export function OrgDocumentUploadStagingArea({
  queue,
  isProcessing,
  processQueue,
  removeQueueItem,
  addFiles,
}: OrgDocumentUploadStagingAreaProps) {
  const [rejectedCount, setRejectedCount] = useState(0);
  const [showQueueLimitMessage, setShowQueueLimitMessage] = useState(false);
  const [queueLimitExceeded, setQueueLimitExceeded] = useState(false);

  const handleFileChange = (files: File | File[] | null) => {
    if (!files) return;

    // Convert to array if single file
    const fileArray = Array.isArray(files) ? files : [files];

    // Use addFiles directly - no mock event needed
    const result = addFiles(fileArray);

    // Handle rejected files
    if (result.rejected > 0) {
      setRejectedCount(result.rejected);
      setTimeout(() => setRejectedCount(0), 5000);
    }

    // Handle queue limit messages
    if (result.queueLimitReached || result.queueLimitExceeded) {
      setShowQueueLimitMessage(true);
      setQueueLimitExceeded(!!result.queueLimitExceeded);
      setTimeout(() => setShowQueueLimitMessage(false), 5000);
    }
  };

  const documentFileFilter = (file: File) => {
    const validExtensions = [
      ".pdf",
      ".ppt",
      ".pptx",
      ".pptm",
      ".doc",
      ".docx",
      ".docm",
      ".json",
    ];
    const fileName = file.name.toLowerCase();
    const maxSize = 50 * 1024 * 1024; // 50MB

    const hasValidExtension = validExtensions.some((ext) =>
      fileName.endsWith(ext)
    );
    const isValidSize = file.size <= maxSize;

    return hasValidExtension && isValidSize;
  };

  return (
    <div className={styles.stagingArea}>
      <FileDropzone
        value={null}
        onChange={handleFileChange}
        instruction="Drop files here to upload"
        subInstruction={`Only PDF, PowerPoint (.ppt, .pptx, .pptm), Word (.doc, .docx, .docm), and JSON files under 50MB are accepted. Maximum ${MAX_QUEUE_SIZE} files can be queued at once.`}
        accept=".pdf,.ppt,.pptx,.pptm,.doc,.docx,.docm,.json"
        multiple={true}
        isDisabled={isProcessing}
        buttonText="Select Files"
        disabledButtonText="Processing..."
        fileFilter={documentFileFilter}
      />

      {rejectedCount > 0 && (
        <div className={styles.warning}>
          {rejectedCount} file(s) were rejected because they don't meet the
          format or size requirements
        </div>
      )}

      {showQueueLimitMessage && (
        <div className={styles.warning}>
          {queueLimitExceeded
            ? `Queue is full. Process current files before adding more (limit: ${MAX_QUEUE_SIZE})`
            : `Some files were not added because queue limit of ${MAX_QUEUE_SIZE} would be exceeded`}
        </div>
      )}

      {queue.length > 0 && (
        <>
          <ul className={styles.files}>
            {queue.map((item, index) => (
              <li key={index} className={styles.queueItem}>
                <div className={styles.fileInfo}>
                  <span>{item.file.name}</span>
                  <span className={`${styles.status} ${styles[item.status]}`}>
                    {item.status}
                  </span>
                  {item.status === "error" && item.error && (
                    <span className={styles.error}>Error: {item.error}</span>
                  )}
                </div>
                <Button
                  variant="tertiary"
                  onPress={() => removeQueueItem(index)}
                  isDisabled={isProcessing && item.status === "uploading"}
                  className={styles.removeButton}
                  style={{ minWidth: "80px" }}
                >
                  Remove
                </Button>
              </li>
            ))}
          </ul>
          <div className={styles.queueCounter}>
            {queue.length} of {MAX_QUEUE_SIZE} files in queue
          </div>
          <ButtonGroup align="right">
            <Button
              variant="primary"
              width="stretch"
              onPress={processQueue}
              isDisabled={isProcessing}
              style={{ minWidth: "120px" }}
            >
              {isProcessing ? "Uploading..." : "Upload"}
            </Button>
          </ButtonGroup>
        </>
      )}
    </div>
  );
}
